import React from "react";
import { Link } from "react-router-dom";
import { Gauge } from "lucide-react";
import { platforms, comparisonPairs } from "../data/mock";

const sortedByRank = [...platforms].sort((a, b) => a.rank - b.rank);

const footerLinkMap = {
  "About Us": "/about",
  "Methodology": "/methodology",
  "Editorial Policy": "/editorial-policy",
  "Disclosure": "/disclosure",
  "Evidence Standards": "/methodology",
  "Corrections": "/corrections",
  "Reviewer Standards": "/about",
  "Affiliate Disclosure": "/disclosure",
  "Contact Editorial": "/contact",
};

const footerColumns = [
  {
    title: "Platforms",
    links: sortedByRank.slice(0, 6).map((p) => ({ label: p.name, to: `/${p.slug}-review` })),
  },
  {
    title: "Comparisons",
    links: [
      ...comparisonPairs.filter((comparison) => comparison.path).slice(0, 4).map((comparison) => ({ label: comparison.label, to: comparison.path })),
      { label: "View All Comparisons", to: "/ecommerce-comparisons" },
    ],
  },
  {
    title: "Resources",
    links: ["About Us", "Methodology", "Editorial Policy", "Disclosure", "Evidence Standards", "Corrections"],
  },
  {
    title: "Trust Center",
    links: ["Reviewer Standards", "Affiliate Disclosure", "Contact Editorial"],
  },
];

const Footer = () => {
  return (
    <footer className="bg-[#111827] text-slate-300 mt-16">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-md bg-indigo-500 flex items-center justify-center">
                <Gauge className="w-5 h-5 text-white" />
              </div>
              <span className="text-white font-bold text-lg">E-Com Benchmark Lab</span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs">
              Compare. Test. Understand. Choose Better. Data-driven reviews and comparisons for the software powering modern commerce.
            </p>
          </div>
          {footerColumns.map((col) => (
            <div key={col.title}>
              <h4 className="text-white text-sm font-semibold mb-3">{col.title}</h4>
              <ul className="space-y-2">
                {col.links.map((l) => {
                  const label = typeof l === "string" ? l : l.label;
                  const to = typeof l === "string" ? footerLinkMap[l] : l.to;
                  return (
                    <li key={label}>
                      {to && to.startsWith("mailto:") ? (
                        <a data-testid={`footer-link-${label.toLowerCase().replaceAll(" ", "-")}`} href={to} className="text-sm text-slate-400 hover:text-white transition-colors">{label}</a>
                      ) : to ? (
                        <Link data-testid={`footer-link-${label.toLowerCase().replaceAll(" ", "-")}`} to={to} className="text-sm text-slate-400 hover:text-white transition-colors">{label}</Link>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 mt-10 pt-6">
          <p className="text-xs text-slate-300 leading-relaxed max-w-4xl font-medium">
            This publication is produced by the creators of Simplestores. That relationship creates a material conflict of interest and is disclosed so readers can account for it.
          </p>
          <p className="text-xs text-slate-500 leading-relaxed max-w-4xl mt-2">
            Numeric product scores are withheld. Published coverage counts indicate cited first-party material only; they are not rankings, quality measures or independent test results.
          </p>
          <div className="flex flex-wrap items-center justify-between mt-4 gap-2">
            <p className="text-xs text-slate-500">© 2026 E-Com Benchmark Lab. All rights reserved.</p>
            <div className="flex gap-4 text-xs text-slate-500">
              <Link data-testid="footer-privacy-link" to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
              <Link data-testid="footer-terms-link" to="/terms-of-use" className="hover:text-white">Terms of Use</Link>
              <Link data-testid="footer-disclosure-link" to="/disclosure" className="hover:text-white">Affiliate Disclosure</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
